import { useRef, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AccountingEngine } from '../services/accountingEngine';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { formatDate } from '../lib/dateUtils';
import { AppModal } from '../components/AppModal';

export const StockItemVouchersView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex, stockItemId }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<any[] | null>(null);

  const data = useLiveQuery(async () => {
    if (!stockItemId) return null;
    return await AccountingEngine.generateStockItemVouchers(stockItemId, fromDate, toDate);
  }, [stockItemId, fromDate, toDate]);

  const vouchers = data?.vouchers || [];
  useWheelNavigation(scrollRef, vouchers.length, setSelectedIndex, false, () => vouchers && vouchers[selectedIndex] && handleItemSelect(vouchers[selectedIndex], selectedIndex));

  const processAction = async (actionKey: string) => {
    const flatItems = vouchers || [];
    const targetItems = selectedIds.size > 0 
      ? flatItems.filter((item: any) => selectedIds.has(item.id))
      : (flatItems[selectedIndex] ? [flatItems[selectedIndex]] : []);

    if (actionKey === 'Space' || actionKey === ' ') {
      const item = flatItems[selectedIndex];
      if (item) {
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(item.id)) next.delete(item.id);
          else next.add(item.id);
          return next;
        });
      }
    } else if (actionKey.toLowerCase() === 'a') {
      handleItemSelect({ name: 'Vouchers' });
    } else if (actionKey.toLowerCase() === 'd') {
      if (targetItems.length === 0) return;
      setPendingDelete(targetItems);
    } else if (actionKey.toLowerCase() === 'r') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        for (const item of targetItems) next.add(item.id);
        return next;
      });
      setSelectedIds(new Set());
      setSelectedIndex(Math.min(flatItems.length - 1, selectedIndex + 1));
    } else if (actionKey.toLowerCase() === 'u') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        if (selectedIds.size > 0) {
          for (const item of targetItems) next.delete(item.id);
        } else {
          const item = flatItems[selectedIndex];
          if (item) next.delete(item.id);
        }
        return next;
      });
      setSelectedIds(new Set());
    } else if (actionKey === 'Enter') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) handleItemSelect(item, selectedIndex);
    } else if (actionKey === 'F2') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'F2', altKey: true }));
    } else if (actionKey === '2') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) {
        handleItemSelect({ name: 'Duplicate Voucher', level: 'duplicate_voucher_form', id: item.id });
      }
    } else if (actionKey.toLowerCase() === 'q' || actionKey === 'Escape') {
      handleItemSelect({ name: 'Quit' });
    }
  };

  const executeDelete = async () => {
    const targetItems = pendingDelete || [];
    const flatItems = vouchers || [];
    setPendingDelete(null);
    try {
      let maxAmt = 0;
      for (const v of targetItems) {
         await AccountingEngine.deleteVoucher(v.id, activeCompany!.id!);
         const amt = v.value ? parseFloat(v.value) : 0;
         if (!isNaN(amt) && amt > maxAmt) maxAmt = amt;
      }

      let msg = targetItems.length > 1
        ? `Moved ${targetItems.length} vouchers to Bin Box.`
        : `Moved voucher ${targetItems[0].vchNo || ''} to Bin Box.`;

      if (maxAmt > 50000) msg += ` (High Value: ${maxAmt.toFixed(2)})`;
      useStore.getState().setGlobalMessage(msg);

      setSelectedIds(new Set());
      setSelectedIndex(Math.max(0, Math.min(flatItems.length - targetItems.length - 1, selectedIndex)));
    } catch (err: any) {
       alert(`Failed to soft-delete voucher: ${err.message}`);
    }
  };

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (pendingDelete) return;

      const viewKeys = [' ', 'Enter', 'Escape', 'F2', 'a','d','r','u','q','2'];
      if (viewKeys.includes(e.key) || viewKeys.includes(e.key.toLowerCase())) {
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.preventDefault();
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
      }

      await processAction(e.key);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [vouchers, selectedIndex, activeCompany, handleItemSelect, setSelectedIndex, removedIds, selectedIds, pendingDelete]);

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Stock Vouchers</span>
        <span className="w-1/3 text-center">{data?.item?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 bg-gray-50">
          <span className="w-[80px]">Date</span>
          <span className="flex-1">Particulars</span>
          <span className="w-[100px]">Vch Type</span>
          <span className="w-[80px]">Vch No.</span>
          <span className="w-[100px] text-right">Quantity</span>
          <span className="w-[80px] text-right">Rate</span>
          <span className="w-[100px] text-right">Value</span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto w-full">
          {vouchers.map((v: any, idx: number) => {
            const isRemoved = removedIds.has(v.id);
            const isSelectedRow = selectedIndex === idx;
            const isChecked = selectedIds.has(v.id);

            let rowClass = 'text-black';
            if (isChecked) {
               rowClass = isSelectedRow ? 'bg-[#1b7070] text-[#ffcc00] font-bold active-row' : 'bg-[#1e8585] text-white font-semibold';
            } else if (isSelectedRow) {
               rowClass = 'bg-[#fff9c4] text-black font-semibold active-row';
            }

            if (isRemoved) {
               rowClass += ' opacity-40 italic line-through text-gray-500';
            }

            return (
            <div 
              key={idx}
              className={`flex justify-between w-full px-2 py-[2px] cursor-pointer border-b border-gray-100 ${rowClass}`}
              onClick={() => { setSelectedIndex(idx); }}
              onDoubleClick={() => !isRemoved && handleItemSelect(v, idx)}
            >
              <span className="w-[80px]">{formatDate(v.date)}</span>
              <span className={`flex-1 ${v.isOpening ? 'italic text-gray-600' : ''}`}>{v.particulars}</span>
              <span className="w-[100px]">{v.vchType}</span>
              <span className="w-[80px]">{v.vchNo}</span>
              <span className={`w-[100px] text-right ${v.type === 'Outward' ? 'text-red-600' : ''}`}>
                {v.quantity.toFixed(2)} {v.type === 'Outward' ? ' Out' : ' In'}
              </span>
              <span className="w-[80px] text-right">{v.rate.toFixed(2)}</span>
              <span className="w-[100px] text-right">{v.value.toFixed(2)}</span>
            </div>
          )})}
        </div>

        <div className="flex justify-between border-t border-gray-400 font-bold px-2 py-1 bg-gray-50">
          <span className="flex-1">Closing Balance</span>
          <span className="w-[100px] text-right">{data?.closingQty.toFixed(2)}</span>
          <span className="w-[180px]"></span>
        </div>
      </div>

      {/* Shortcuts bar */}
      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto text-[11px]">
        {[
          { key: 'Q', label: 'Quit' }, { key: 'Enter', label: 'Alter' }, { key: 'Space', label: 'Select' },
          { key: 'A', label: 'Add Vch' }, { key: '2', label: 'Duplicate Vch' },
          { key: 'D', label: 'Delete' }, { key: 'R', label: 'Remove Line' },
          { key: 'U', label: 'Restore Line' }, { key: 'F2', label: 'Period' }
        ].map((sc, i) => (
          <div key={i} className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100"
            onClick={() => processAction(sc.key)}>
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">{sc.key[0]}</span>
              {sc.key.length > 1 && <span className="text-[#2a62a3] font-bold">{sc.key.substring(1)}</span>}
              <span className="text-[#2a62a3] font-bold">:</span>
              <span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
      </div>

      <AppModal
        open={!!pendingDelete}
        title="Move to Bin Box"
        type="warning"
        message={
          pendingDelete && pendingDelete.length > 1
            ? `Move ${pendingDelete.length} vouchers to Bin Box?`
            : `Move voucher ${pendingDelete?.[0]?.vchNo || ''} to Bin Box?`
        }
        confirmLabel="Move to Bin Box"
        cancelLabel="Cancel"
        onConfirm={executeDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};
